import { useEffect, useMemo, useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import EthDater from 'ethereum-block-by-date';
import useRaffles from './useRaffles';
import useRainbows from '../hooks/useRainbows';
import { ethers } from 'ethers';

const BUFFER = 300; // 5 minutes

const raffleAccount = process.env.NEXT_PUBLIC_RAFFLE_ACCOUNT;

const useRaffleTickets = (raffleKey) => {
  const { library } = useWeb3React();
  const raffle = useRaffles(raffleKey);
  const rainbows = useRainbows();

  const [transactions, setTransactions] = useState({});
  const [tickets, setTickets] = useState([]);

  const [intervalReady, setIntervalReady] = useState(false);
  const [ready, setReady] = useState(false);

  const [startBlock, setStartBlock] = useState();
  const [endBlock, setEndBlock] = useState();

  useEffect(() => {
    setIntervalReady(false);
    let ignore = false;

    if (raffle && library) {
      const raffleStart = new Date(raffle.start_time);
      const raffleEnd = new Date(raffle.end_time);

      raffleStart.setSeconds(raffleStart.getSeconds() - BUFFER);
      raffleEnd.setSeconds(raffleEnd.getSeconds() + BUFFER);

      const dater = new EthDater(library);

      Promise.all([
        dater.getDate(raffleStart, false),
        dater.getDate(raffleEnd, true),
      ]).then(([startBlock, endBlock]) => {
        if (!ignore) {
          setStartBlock(startBlock.block);
          setEndBlock(endBlock.block);

          setIntervalReady(true);
        }
      });
    }

    return () => {
      ignore = true;
    };
  }, [raffle, library]);

  useEffect(() => {
    let ignore = false;
    setReady(false);
    setTransactions({});
    if (rainbows && intervalReady) {
      const parseEvent = (event) => {
        const { transactionHash, blockNumber, logIndex } = event;
        const [from, to, value] = event.args;
        return {
          transactionHash,
          blockNumber,
          logIndex,
          args: { from, to, value },
        };
      };

      const filter = rainbows.filters.Transfer(null, raffleAccount);
      rainbows.queryFilter(filter, startBlock, endBlock).then((events) => {
        const transactions = events.map(parseEvent);
        setTransactions(_.keyBy(transactions, 'transactionHash'));
        setReady(true);
      });

      rainbows.on(filter, (_from, _to, _value, event) => {
        if (!ignore) {
          const transaction = parseEvent(event);
          setTransactions((transactions) => ({
            ...transactions,
            [transaction.transactionHash]: transaction,
          }));
        }
      });

      return () => {
        ignore = true;
        rainbows.removeAllListeners(filter);
      };
    }
  }, [rainbows, intervalReady, startBlock, endBlock]);

  useEffect(() => {
    setTickets([]);
    if (raffle && !raffle.disable_ticket_sales) {
      const ticketPrice = ethers.constants.WeiPerEther.mul(raffle.ticket_price);
      const tickets = _.sortBy(Object.values(transactions), [
        'blockNumber',
        'logIndex',
      ]).map((event) => {
        const { from, value } = event.args;
        const ticketCount = value.div(ticketPrice).toNumber();

        return { from, ticketCount };
      });
      setTickets(tickets);
    }
  }, [transactions, raffle]);

  const result = useMemo(() => ({ ready, tickets }), [ready, tickets]);

  return result;
};

export default useRaffleTickets;
